/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Components/AzureSubscriptionPickList" />
import * as Resources from "Build/DesignerExtensions/YamlSnippetForm/Resources";
import * as React from "react";

import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Button } from "VSSUI/Button";
import { IListBoxItem, ListBoxItemType, LoadingCell } from "VSSUI/ListBox";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import { ObservableValue } from "VSS/Core/Observable";
import { FormItem } from "VSSUI/FormItem";

import { IServiceConnectionProvider } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import { AzureRMConnectionType } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Constants";
import { AzureSubscription, ServiceEndpoint } from "DistributedTask/Client/Contracts/TaskAgent";
import { YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";
import { EnhancedLabel } from "Build/DesignerExtensions/YamlSnippetForm/Components/EnhancedLabel";

export interface IAzureSubscriptionPickListProps {
    inputDefinition: YamlSnippetInput;
    serviceConnectionProvider: IServiceConnectionProvider;
    getValue(name: string): string;
    onValueChanged(name: string, value: string, valueAlias?: string): void;
    onError(location: string, error: Error): void;
}

export interface IAzureSubscriptionPickListState {
    authorizing?: boolean;
    needAuthorizing?: boolean;
}

const connectionsGroupKey = "connections";
const subscriptionsGroupKey = "subscriptions";

/**
 * AzureSubscriptionPickList is a component used to pick an Azure Resource Manager Service Connection. It can do this either
 * by selecting one that has already been set up, or you can pick an Azure Subscription that you have access to. In the latter case,
 * there is an Authorize button which will create the service connection for you and then select it in the list.
 */
export class AzureSubscriptionPickList extends React.PureComponent<IAzureSubscriptionPickListProps, IAzureSubscriptionPickListState> {
    private _error = new ObservableValue<boolean>(false);
    private _message = new ObservableValue<string | undefined>(undefined);
    private _selection = new DropdownSelection();
    private _initiallySelectedName: string;
    private _itemProvider = new GroupedItemProvider([], [
        {id: connectionsGroupKey, loading: true, name: Resources.AzureConnectionGroupTitle}, 
        {id: subscriptionsGroupKey, loading: true, name: Resources.AzureSubscriptionGroupTitle}
    ], true);

    constructor(props: IAzureSubscriptionPickListProps) {
        super(props);

        this._initiallySelectedName = this.props.getValue(this.props.inputDefinition.name);

        this.state = {
            authorizing: false,
            needAuthorizing: false
        };
    }

    public render(): JSX.Element {
        const showAuthorizeButton = this.state.needAuthorizing;
        const authorizeButtonLabel = this.state.authorizing ? Resources.Authorizing : Resources.Authorize;
        const showAuthorizeSpinner = this.state.authorizing;
        const inputDefinition = this.props.inputDefinition;

        return <div className={"flex-column"}>
            <FormItem message={this._message} 
                      error={this._error} 
                      label={<EnhancedLabel title={inputDefinition.label} 
                      isRequired={inputDefinition.required} 
                      documentation={inputDefinition.helpMarkdown}/>} >
                <Dropdown noItemsText={Resources.NoAzureRMResults} items={this._itemProvider} selection={this._selection} onSelect={this._onSelectionChanged} />
            </FormItem>
            {showAuthorizeButton && 
            <Button className={"authorize-button flex-self-start"} 
                    onClick={this._onAuthorize} 
                    primary={true} 
                    disabled={this.state.authorizing}>
                {showAuthorizeSpinner && <Spinner size={SpinnerSize.small} />}
                {authorizeButtonLabel}
            </Button>}
        </div>
    }

    public componentDidMount() {
        this._loadSubscriptions();
    }

    private async _loadSubscriptions() {
        try {
            const subscriptionsPromise = this.props.serviceConnectionProvider.getAzureSubscriptions();
            const existingConnectionsPromise = this.props.serviceConnectionProvider.getServiceConnections(AzureRMConnectionType);

            const subscriptions = await subscriptionsPromise;
            const connections = await existingConnectionsPromise;
            this._buildConnectionOptions(subscriptions, connections);
        } catch (error) {
            this._error.value = true;
            this._message.value = error.message;
            this.props.onError("AzureSubscriptionPickList._loadSubscriptions", error);
        }
    }

    private _buildConnectionOptions(subscriptions: AzureSubscription[], connections: ServiceEndpoint[]) {
        let connectionItems: IListBoxItem[] = connections.map(c => { return {
            id: c.id,
            text: c.name,
            groupId: connectionsGroupKey,
            data: c
        } as IListBoxItem });
        
        // If the user provided an option for this that we didn't find a match for, let them still pick the one they had
        // They must have specified a service connection, not a subscription, so only match against those. (subscriptions might have the same name but we don't count those)
        if (this._initiallySelectedName && !connectionItems.find(item => item.text === this._initiallySelectedName)) {
            connectionItems.push({
                id: this._initiallySelectedName,
                text: this._initiallySelectedName,
                groupId: connectionsGroupKey,
                data: undefined
            });
        }

        connectionItems.sort((a, b) => {
            return a.text!.localeCompare(b.text!, navigator.language);
        });

        let subscriptionItems: IListBoxItem[] = subscriptions.map(s => { return {
            id: s.subscriptionId,
            text: this._getSubscriptionDisplayName(s),
            groupId: subscriptionsGroupKey,
            data: s
        } as IListBoxItem });

        subscriptionItems.sort((a, b) => {
            return a.text!.localeCompare(b.text!, navigator.language);
        });

        this._itemProvider.removeAll();
        if (connectionItems) {
            this._itemProvider.pushGroups({
                id: connectionsGroupKey,
                name: Resources.AzureConnectionGroupTitle
            })
            this._itemProvider.push(...connectionItems);
        }

        if (subscriptionItems) {
            this._itemProvider.pushGroups({
                id: subscriptionsGroupKey,
                name: Resources.AzureSubscriptionGroupTitle
            })
            this._itemProvider.push(...subscriptionItems);
        }

        if (this._initiallySelectedName) {
            this._selection.select(this._itemProvider.value.findIndex(i => i.text === this._initiallySelectedName || i.id === this._initiallySelectedName));
        }
    }

    private _onSelectionChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem) => {
        if (item.groupId === subscriptionsGroupKey) {
            this._error.value = false;
            this._message.value = Resources.NeedsAuthorizing;
            this.setState({
                authorizing: false,
                needAuthorizing: true
            })
        }
        else if (item.groupId === connectionsGroupKey) {
            this._error.value = false;
            this._message.value = undefined;
            this.props.onValueChanged(this.props.inputDefinition.name, item.id, item.text);
            this.setState({
                authorizing: false,
                needAuthorizing: false
            })
        }
    }

    private _onAuthorize = async (): Promise<void> => {
        try {
            if (!this._selection.selectedCount) {
                return Promise.resolve(undefined);
            }

            const subscriptionItem = this._itemProvider.value[this._selection.value[0].beginIndex];

            if (!subscriptionItem.data) {
                return Promise.resolve(undefined);
            }

            const subscription = subscriptionItem.data as AzureSubscription;

            this.setState({
                authorizing: true
            });

            let subscriptionCopy = {...subscription} as AzureSubscription;
            let connectionName = this._getSubscriptionDisplayName(subscriptionCopy);
            let connectionNameNumber = 1;
            const currentConnectionNames = this._itemProvider.value.filter(i => i.groupId === connectionsGroupKey).map(c => c.text);
            while (currentConnectionNames.indexOf(connectionName) >= 0) {
                subscriptionCopy.displayName = subscription.displayName + '(' + connectionNameNumber + ')';
                connectionName = this._getSubscriptionDisplayName(subscriptionCopy);
                connectionNameNumber++;
            }

            const newEndpoint = await this.props.serviceConnectionProvider.createAzureRMConnection(subscriptionCopy);
            this._itemProvider.push({
                id: newEndpoint.id,
                text: newEndpoint.name,
                groupId: connectionsGroupKey,
                data: newEndpoint
            })
            this._selection.select(this._itemProvider.value.findIndex(i => i.id === newEndpoint.id));

            this.props.onValueChanged(this.props.inputDefinition.name, newEndpoint.id, newEndpoint.name);

            this._error.value = false;
            this._message.value = undefined;
            this.setState({
                authorizing: false,
                needAuthorizing: false
            });
        } catch (error) {
            this._error.value = true;
            this._message.value = error.message;
            this.setState({
                authorizing: false
            });

            this.props.onError("AzureSubscriptionPickList._onAuthorize", error);
        }
    }

    private _getSubscriptionDisplayName(subscription: AzureSubscription): string {
        return subscription.displayName + '(' + subscription.subscriptionId + ')';
    }
}
